import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { AuthContext } from '../../../../context/AuthContext';
import { getSafetyBasicInfo } from '../../../../api/safetyAssessApi';

function RiskViewModal({ initialData, onClose, onEdit, onDelete, hasPermission = false }) {
  const { token } = useContext(AuthContext);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [useProcess, setUseProcess] = useState(true);
  const [evalMethod, setEvalMethod] = useState('3X3');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const basicInfo = await getSafetyBasicInfo(token);
        if (basicInfo && basicInfo.useProcess !== undefined) {
          setUseProcess(basicInfo.useProcess);
        }
        if (basicInfo && basicInfo.evalMethod) {
          setEvalMethod(basicInfo.evalMethod);
        }
      } catch (err) {
        console.error('설정 조회 실패:', err);
      }
    };
    fetchConfig();
  }, [token]);

  const getBadgeStyleAndLabel = (score) => {
    if (!score) return { color: '#64748b', text: '-', label: '미평가' };
    const s = parseInt(score, 10);
    if (evalMethod === '3X3') {
      if (s === 1 || s === 2) return { color: '#10b981', text: `${s}점`, label: '하' };
      if (s === 3 || s === 4) return { color: '#f59e0b', text: `${s}점`, label: '중' };
      return { color: '#ef4444', text: `${s}점`, label: '상' };
    } else {
      if (s <= 4) return { color: '#3b82f6', text: `${s}점`, label: '낮음' };
      if (s <= 8) return { color: '#10b981', text: `${s}점`, label: '보통' };
      if (s <= 16) return { color: '#f59e0b', text: `${s}점`, label: '높음' };
      return { color: '#ef4444', text: `${s}점`, label: '치명적' };
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  if (!initialData) return null;

  const ev = initialData.evaluation || {};
  const photos = ev.photos || [];
  const beforePhoto = photos.find(p => p.type === 'BEFORE');
  const afterPhoto = photos.find(p => p.type === 'AFTER');

  const writeName = ev.writeName;
  const createdAt = ev.createdAt;

  const displayProcessName = (initialData.processName === '사업장 전역 (공통)' || !initialData.processName)
    ? '사업장 공통'
    : initialData.processName;

  return ReactDOM.createPortal(
    <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9998, overflow: 'hidden' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '1200px', width: '95%' }}>
        <div className="modal-content" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
          <div className="modal-header d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', color: '#fff', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', padding: '16px 24px' }}>
            <h5 className="modal-title fw-bold m-0"><i className="fas fa-eye"></i> 위험성 평가 상세 정보</h5>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {writeName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#f8fafc', fontWeight: '500' }}>
                  <i className="fas fa-user" style={{ fontSize: '13px' }}></i>
                  {writeName}
                </div>
              )}
              {writeName && createdAt && (
                <div style={{ width: '1px', height: '14px', backgroundColor: 'rgba(255,255,255,0.4)', margin: '0 2px' }} />
              )}
              {createdAt && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#f8fafc', fontWeight: '500' }}>
                  <i className="far fa-clock" style={{ fontSize: '13px' }}></i>
                  {formatDateTime(createdAt)}
                </div>
              )}
              {(writeName || createdAt) && <div style={{ width: '1px', height: '14px', backgroundColor: 'rgba(255,255,255,0.4)', margin: '0 2px' }} />}
              <button type="button" className="btn-close btn-close-white" onClick={onClose} style={{ boxShadow: 'none' }}></button>
            </div>
          </div>
          
          <div className="modal-body" style={{ padding: '20px 30px', height: '75vh', minHeight: '450px', overflowY: 'auto', backgroundColor: '#f8fafc' }}>
            <div className="row g-3">
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <div className="fw-bold text-secondary mb-2 d-block" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-list-alt me-1 text-primary"></i> 위험성평가명
                  </div>
                  <div className="text-dark fw-bold px-3 py-2 rounded" style={{ fontSize: '0.85rem', backgroundColor: '#f1f5f9' }}>
                    {initialData.assessmentName || '위험성평가 정보 없음'}
                  </div>
                </div>
              </div>

              {/* 분류 및 공정 정보 */}
              {useProcess ? (
                <>
                  <div className="col-md-4">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <div className="fw-bold text-secondary mb-2 d-block" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-folder me-1 text-primary"></i> 유해위험 대분류
                      </div>
                      <div className="text-dark fw-bold px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>{initialData.categoryName || '-'}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <div className="fw-bold text-secondary mb-2 d-block" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-exclamation-triangle me-1 text-primary"></i> 유해위험 원인
                      </div>
                      <div className="text-dark fw-bold px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>{initialData.causeName || '-'}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <div className="fw-bold text-secondary mb-2 d-block" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-tasks me-1 text-primary"></i> 대상 공정
                      </div>
                      <div className="text-dark fw-bold px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>{displayProcessName}</div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="col-md-6">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <div className="fw-bold text-secondary mb-2 d-block" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-folder me-1 text-primary"></i> 유해위험 대분류
                      </div>
                      <div className="text-dark fw-bold px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>{initialData.categoryName || '-'}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <div className="fw-bold text-secondary mb-2 d-block" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-exclamation-triangle me-1 text-primary"></i> 유해위험 원인
                      </div>
                      <div className="text-dark fw-bold px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>{initialData.causeName || '-'}</div>
                    </div>
                  </div>
                </>
              )}

              {/* 요인 및 대책 */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <div className="fw-bold text-secondary mb-2 d-block" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-search me-1 text-danger"></i> 유해위험요인 파악
                  </div>
                  <div className="p-2 border rounded bg-light text-dark" style={{ whiteSpace: 'pre-wrap', borderColor: '#cbd5e1', fontSize: '0.825rem', minHeight: '100px' }}>{ev.currentMeasure || '-'}</div>
                </div>
              </div>
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <div className="fw-bold text-secondary mb-2 d-block" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-check-circle me-1 text-success"></i> 개선(감소)대책 수립
                  </div>
                  <div className="p-2 border rounded bg-light text-dark" style={{ whiteSpace: 'pre-wrap', borderColor: '#cbd5e1', fontSize: '0.825rem', minHeight: '100px' }}>{ev.action || '-'}</div>
                </div>
              </div>

              {/* 개선 전/후 위험성 */}
              <div className="col-md-6">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <h6 className="fw-bold text-secondary mb-3" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-shield-alt me-1 text-danger"></i> 개선 전 위험성
                  </h6>
                  <div className="p-3 rounded-2" style={{ background: '#fff1f2', border: '1px solid #fda4af' }}>
                    <div className="d-flex align-items-center justify-content-around text-center" style={{ fontSize: '0.825rem' }}>
                      <div style={{ flex: 1, borderRight: '1px solid #fda4af' }}>
                        <span className="text-secondary">빈도</span> : <strong className="text-danger">{ev.freqBefore || '-'}</strong>
                      </div>
                      <div style={{ flex: 1, borderRight: '1px solid #fda4af' }}>
                        <span className="text-secondary">강도</span> : <strong className="text-danger">{ev.sevBefore || '-'}</strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        <span className="text-secondary">위험도</span> : <span className="badge" style={{ fontSize: '0.825rem', backgroundColor: getBadgeStyleAndLabel(ev.riskBefore).color, color: '#fff', verticalAlign: 'middle' }}>{getBadgeStyleAndLabel(ev.riskBefore).text}</span>
                        {ev.riskBefore && (
                          <span className="ms-1 fw-bold text-muted" style={{ fontSize: '0.825rem', verticalAlign: 'middle' }}>
                            ({getBadgeStyleAndLabel(ev.riskBefore).label})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <h6 className="fw-bold text-secondary mb-3" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-shield-alt me-1 text-success"></i> 개선 후 위험성
                  </h6>
                  <div className="p-3 rounded-2" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <div className="d-flex align-items-center justify-content-around text-center" style={{ fontSize: '0.825rem' }}>
                      <div style={{ flex: 1, borderRight: '1px solid #bbf7d0' }}>
                        <span className="text-secondary">빈도</span> : <strong className="text-success">{ev.freqAfter || '-'}</strong>
                      </div>
                      <div style={{ flex: 1, borderRight: '1px solid #bbf7d0' }}>
                        <span className="text-secondary">강도</span> : <strong className="text-success">{ev.sevAfter || '-'}</strong>
                      </div>
                      <div style={{ flex: 1 }}>
                        <span className="text-secondary">위험도</span> : <span className="badge" style={{ fontSize: '0.825rem', backgroundColor: getBadgeStyleAndLabel(ev.riskAfter).color, color: '#fff', verticalAlign: 'middle' }}>{getBadgeStyleAndLabel(ev.riskAfter).text}</span>
                        {ev.riskAfter && (
                          <span className="ms-1 fw-bold text-muted" style={{ fontSize: '0.825rem', verticalAlign: 'middle' }}>
                            ({getBadgeStyleAndLabel(ev.riskAfter).label})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 담당자 정보 */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <div className="fw-bold text-secondary mb-2 d-block" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-user me-1 text-secondary"></i> 담당 부서 / 조치 담당자
                  </div>
                  <div className="p-3 border rounded text-dark" style={{ height: '110px', overflowY: 'auto', borderColor: '#cbd5e1', fontSize: '0.825rem', backgroundColor: '#f1f5f9', display: (!ev.assignees || ev.assignees.length === 0) ? 'flex' : 'block', alignItems: 'center', justifyContent: 'center' }}>
                    {ev.assignees && ev.assignees.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {ev.assignees.map((a, idx) => (
                          <span key={idx} className="badge bg-white text-dark border p-2 fw-normal" style={{ fontSize: '0.8rem', borderColor: '#cbd5e1' }}>
                            {a.name}{a.position ? ` / ${a.position}` : ''}{a.department ? ` (${a.department})` : ''}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted fw-semibold" style={{ fontSize: '0.85rem' }}>담당자가 지정되지 않았습니다.</span>
                    )}
                  </div>
                </div>
              </div>

              {/* 조치 상태 및 일정 */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <div className="d-flex align-items-center flex-wrap gap-4">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-secondary" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        <i className="fas fa-clipboard-check me-1 text-secondary"></i>조치 완료 상태 :
                      </span>
                      {(() => {
                        const actionStatus = ev.actionStatus || 'pending';
                        const badgeClass = 
                          actionStatus === 'completed' ? 'bg-success' :
                          actionStatus === 'in_progress' ? 'bg-warning text-dark' : 'bg-secondary';
                        const statusLabel = 
                          actionStatus === 'completed' ? '완료' :
                          actionStatus === 'in_progress' ? '진행중' : '미완료 (대기)';
                        return (
                          <span className={`badge ${badgeClass}`} style={{ fontSize: '0.825rem', padding: '6px 12px' }}>
                            {statusLabel}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-secondary" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        <i className="fas fa-calendar-alt me-1 text-secondary"></i>조치예정일 :
                      </span>
                      <div className="px-2 py-1 border rounded bg-light text-dark text-center" style={{ width: '110px', fontSize: '0.825rem', borderColor: '#cbd5e1' }}>{ev.dueDate ? ev.dueDate.split('T')[0] : '-'}</div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-secondary" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        <i className="fas fa-calendar-check me-1 text-secondary"></i>조치완료일 :
                      </span>
                      <div className="px-2 py-1 border rounded bg-light text-dark text-center" style={{ width: '110px', fontSize: '0.825rem', borderColor: '#cbd5e1' }}>{ev.doneDate ? ev.doneDate.split('T')[0] : '-'}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 전/후 이미지 */}
              <div className="col-12">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <span className="fw-bold text-secondary mb-3" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-image me-1 text-secondary"></i> 개선 전 사진
                      </span>
                      <div className="position-relative w-100 rounded-3 overflow-hidden border text-center" style={{ borderColor: '#e2e8f0' }}>
                        {beforePhoto ? (
                          <img 
                            src={beforePhoto.url} 
                            alt="개선 전" 
                            style={{ width: '100%', height: '400px', objectFit: 'contain', cursor: 'pointer' }} 
                            onClick={() => setLightboxUrl(beforePhoto.url)}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center bg-light text-muted" style={{ height: '400px', fontSize: '0.825rem' }}>
                            등록된 사진 없음
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                      <span className="fw-bold text-secondary mb-3" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-image me-1 text-success"></i> 개선 후 사진
                      </span>
                      <div className="position-relative w-100 rounded-3 overflow-hidden border text-center" style={{ borderColor: '#e2e8f0' }}>
                        {afterPhoto ? (
                          <img 
                            src={afterPhoto.url} 
                            alt="개선 후" 
                            style={{ width: '100%', height: '400px', objectFit: 'contain', cursor: 'pointer' }} 
                            onClick={() => setLightboxUrl(afterPhoto.url)}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center bg-light text-muted" style={{ height: '400px', fontSize: '0.825rem' }}>
                            등록된 사진 없음
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="modal-footer" style={{ borderTop: '1px solid #cbd5e1', padding: '10px 24px', display: 'flex', gap: '3px', justifyContent: 'flex-end' }}>
            {hasPermission && onEdit && <button type="button" className="btn btn-primary" style={{ fontWeight: 'bold' }} onClick={() => onEdit(initialData)}>수정</button>}
            {hasPermission && onDelete && <button type="button" className="btn btn-danger" style={{ fontWeight: 'bold' }} onClick={() => onDelete(initialData)}>삭제</button>}
            <button type="button" className="btn btn-secondary" style={{ fontWeight: 'bold' }} onClick={onClose}>닫기</button>
          </div>
        </div>
      </div>

      {/* 이미지 라이트박스 */}
      {lightboxUrl && (
        <div 
          onClick={() => setLightboxUrl(null)} 
          style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out'
          }}
        >
          <button 
            style={{ position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none', color: '#fff', fontSize: '2rem', cursor: 'pointer' }}
            onClick={() => setLightboxUrl(null)}
          >
            <i className="fas fa-times"></i>
          </button>
          <img 
            src={lightboxUrl} 
            alt="원본 보기" 
            style={{ maxWidth: '90%', maxHeight: '85vh', objectFit: 'contain', borderRadius: '4px', boxShadow: '0 10px 25px rgba(0,0,0,0.5)' }} 
          />
        </div>
      )}
    </div>,
    document.body
  );
}

export default RiskViewModal;
